<script lang="ts" setup>
import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDateTime, deepParseDates } from '@/utils/date-utils';
import { ComputeTaskUtils } from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import { useQuery } from '@vue/apollo-composable';
import { useVisibleQueryPolling } from '@/composables/visible-query-polling';
import {
  BucketType,
  ComputeTasksDocument,
  ComputeTasksLiteDocument,
  ComputeTaskType,
  ProjectType,
} from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BModal,
  BPagination,
  BProgress,
  BProgressBar,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import {
  IS_DEBUG_MODE,
  COMPUTE_TASK_STATUS,
  COMPUTE_TASK_TYPES,
  PAGING_ITEMS_PER_PAGE,
} from '@/constants';
import { useUserStore } from '@/store/user';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { formatFileSize } from '@/utils/file-utils';
import { openRouterLink } from '@/utils/router-utils';

const userStore = useUserStore();
const router = useRouter();

// --- Component Properties ---
const props = defineProps({
  // component properties
  parentName: { type: String as PropType<string> },
  // optional placeholder
  placeholder: { type: String as PropType<string | null>, default: null },
  // search filter
  acrossUsers: { type: Boolean, default: false },
  taskId: { type: Number as PropType<number | null>, default: null },
  parentId: { type: Number as PropType<number | null>, default: null },
  userId: { type: Number as PropType<number | null>, default: null },
  userRole: { type: String as PropType<string | null>, default: null },
  projectId: { type: Number as PropType<number | null>, default: null },
  bucketId: { type: Number as PropType<number | null>, default: null },
  taskType: { type: String as PropType<string | null>, default: null },
  taskTypes: { type: Array as PropType<string[] | null>, default: null },
  status: { type: String as PropType<string | null>, default: null },
  namePattern: { type: String as PropType<string | null>, default: null },
  descriptionPattern: {
    type: String as PropType<string | null>,
    default: null,
  },
  // date & time filters
  createdAfter: { type: String as PropType<string | null>, default: null },
  createdBefore: { type: String as PropType<string | null>, default: null },
  updatedAfter: { type: String as PropType<string | null>, default: null },
  updatedBefore: { type: String as PropType<string | null>, default: null },
  completedBefore: { type: String as PropType<string | null>, default: null },
  completedAfter: { type: String as PropType<string | null>, default: null },
  abortedAfter: { type: String as PropType<string | null>, default: null },
  abortedBefore: { type: String as PropType<string | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  pagingHideControls: { type: Boolean, default: false },
  // additinal component props
  transparentStyle: { type: Boolean, default: false },
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  preventRedirectToBucket: {
    type: Boolean,
    default: false,
  },
  showBucketButton: {
    type: Boolean,
    default: true,
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
  showAbortButton: {
    type: Boolean,
    default: true,
  },
  queryDocument: {
    type: Object as PropType<
      typeof ComputeTasksDocument | typeof ComputeTasksLiteDocument
    >,
    default: ComputeTasksDocument,
  },
});

// --- Component State ---
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});
const showPlaceholderRow = computed(
  () => pagingPageCurrent.value === 1 && props.placeholder
);

// --- Emits ---
const emits = defineEmits<{
  (e: 'bucket-request', bucketId: number): void;
}>();

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

const selectedTask = ref<ComputeTaskType | null>(null);
const showConfirmAbortModal = ref<boolean>(false);

const taskFields = [
  {
    key: 'createdAt',
    label: 'Created At',
    tdClass: 'align-middle',
    sortable: false,
    formatter: formatDateTime,
  },
  {
    key: 'name',
    label: props.taskNameColumnTitle,
    sortable: false,
    tdClass: 'align-middle',
  },
  { key: 'status', label: 'Status', sortable: false, tdClass: 'align-middle' },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

const tooltipFieldsFtpTransfer = [
  {
    key: 'filename',
    label: 'File',
    sortable: false,
    tdClass: 'align-middle text-start',
    thClass: 'text-start',
  },
  {
    key: 'progress',
    label: 'Progress',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
];

const tooltipFieldsBasespaceTransfer = [
  {
    key: 'fileName',
    label: 'File',
    sortable: false,
    tdClass: 'align-middle text-start',
    thClass: 'text-start',
  },
  {
    key: 'size',
    label: 'Size',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
  {
    key: 'progress',
    label: 'Progress',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
];

// --- Query Compute Tasks ---
const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  query: taskPollingQuery,
} = useQuery(props.queryDocument, {
  acrossUsers: props.acrossUsers,
  id: props.taskId,
  parentId: props.parentId,
  userId: props.userId,
  userRole: props.userRole,
  projectId: props.projectId,
  bucketId: props.bucketId,
  taskType: props.taskType,
  taskTypes: props.taskTypes,
  status: props.status,
  namePattern: props.namePattern,
  descriptionPattern: props.descriptionPattern,
  // date & time filters
  createdAfter: props.createdAfter,
  createdBefore: props.createdBefore,
  updatedAfter: props.updatedAfter,
  updatedBefore: props.updatedBefore,
  completedBefore: props.completedBefore,
  completedAfter: props.completedAfter,
  abortedAfter: props.abortedAfter,
  abortedBefore: props.abortedBefore,
  // paging specification
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: props.pagingItemsPerPage,
});
useVisibleQueryPolling(taskPollingQuery);

onTasksQueryDone((res) => {
  console.debug('Tasks response retrieved');
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching tasks (onTasksQueryError):', err.message);
});

function requestRefetchTasks() {
  // ensure integer/null values where needed
  const intTaskId = props.taskId ? parseInt(props.taskId.toString()) : null;
  const intParentId = props.parentId
    ? parseInt(props.parentId.toString())
    : null;
  const intUserId = props.userId ? parseInt(props.userId.toString()) : null;
  const intProjectId = props.projectId
    ? parseInt(props.projectId.toString())
    : null;
  const intBucketId = props.bucketId
    ? parseInt(props.bucketId.toString())
    : null;

  // refetch the tasks with the current filters
  refetchTasks({
    acrossUsers: props.acrossUsers,
    id: intTaskId,
    parentId: intParentId,
    userId: intUserId,
    userRole: props.userRole,
    projectId: intProjectId,
    bucketId: intBucketId,
    taskType: props.taskType,
    taskTypes: props.taskTypes,
    status: props.status,
    namePattern: props.namePattern,
    descriptionPattern: props.descriptionPattern,
    // date & time filters
    createdAfter: props.createdAfter,
    createdBefore: props.createdBefore,
    updatedAfter: props.updatedAfter,
    updatedBefore: props.updatedBefore,
    completedBefore: props.completedBefore,
    completedAfter: props.completedAfter,
    abortedAfter: props.abortedAfter,
    abortedBefore: props.abortedBefore,
    // paging specification
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: props.pagingItemsPerPage,
  })
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchTasks();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    requestRefetchTasks();
  }
}

// --- Compute Task Abort Request ---
function taskAbortRequested(task: ComputeTaskType) {
  if (!task.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
  selectedTask.value = null;
}

// --- Tooltip functions ---
const tooltipVisibility = reactive<{ [taskId: number]: boolean }>({});

function showTooltip(taskId: number) {
  // Hide all other tooltips.
  for (const key in tooltipVisibility) {
    const id = Number(key);
    if (id !== taskId) {
      tooltipVisibility[id] = false;
    }
  }
  tooltipVisibility[taskId] = true;
}

function hideAllTooltips() {
  for (const key in tooltipVisibility) {
    const id = Number(key);
    tooltipVisibility[id] = false;
  }
}

function bucketRequested(bucket: BucketType, index?: number, evt?: MouseEvent) {
  if (bucket && bucket.id) {
    // emit the event to the parent component
    emits('bucket-request', bucket.id);

    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt && !props.preventRedirectToBucket) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: props.parentName,
          },
        },
        router,
        evt
      );
    }
  }
}

function projectRequested(
  project: ProjectType,
  index?: number,
  evt?: MouseEvent
) {
  if (project && project.id) {
    openRouterLink(
      {
        name: 'ProjectDetail',
        params: { id: project.id },
        query: {
          returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
          returnLabel: props.parentName,
        },
      },
      router,
      evt
    );
  }
}

// --- Expose Properties to the Outside ---
defineExpose({
  tasks,
  tasksQueryInProgress,
  tasksQueryError,
  requestSearch,
});

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

function showExecDetails(task: ComputeTaskType) {
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (task.attributes && (!userStore.admin || !userStore.developer)) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}
</script>
<template>
  <div>
    <!-- loading and error alerts -->
    <b-alert v-if="tasksQueryInProgress" variant="info">Loading...</b-alert>
    <b-alert v-if="tasksQueryError" variant="danger">
      {{ tasksQueryError.message }}
      <b-button
        @click="requestRefetchTasks"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- empty list -->
    <b-alert v-model="noTasks" variant="secondary">
      <span v-if="tasksQueryInProgress">
        Loading tasks <b-spinner small />
      </span>
      <span v-else> {{ props.messageNoItemsFound }} </span>
    </b-alert>

    <!-- Paging (above) -->
    <BPagination
      v-if="
        !props.pagingHideControls &&
        pagingItemsPerPage > 0 &&
        pagingTotalRows > props.pagingItemsPerPage
      "
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
      class="mb-2"
    />

    <!-- list tasks -->
    <BCard
      v-if="!tasksQueryError && tasks.length > 0"
      :class="{ 'transparent-card': props.transparentStyle }"
    >
      <b-table
        :items="tasks"
        :fields="taskFields"
        :busy="tasksQueryInProgress"
        :loading-text="'Loading tasks...'"
        @mouseleave="hideAllTooltips"
        :striped="!props.transparentStyle"
        thead-tr-class="transparent-table"
        tbody-tr-class="transparent-table"
        :class="[
          'browser-table',
          'm-0',
          {
            'table-transparent': props.transparentStyle,
          },
        ]"
        small
        responsive
        hover
      >
        <!-- Placeholders Row -->
        <template #top-row v-if="showPlaceholderRow">
          <td class="align-middle py-2 px-1 table-info">
            {{ formatDateTime(new Date(Date.now())) }}
          </td>
          <td class="align-middle py-2 px-1 table-info">
            {{ props.placeholder }}
          </td>
          <td class="align-middle text-center py-2 px-1 table-info">
            <font-awesome-icon icon="spinner" spin class="me-2" />
          </td>
          <td class="align-middle text-center py-2 px-1 table-info"></td>
        </template>
        <!-- Results / Progress -->
        <template #cell(status)="data">
          <!-- Progress Bar with Tooltip -->
          <b-progress
            max="100"
            @mouseenter="showTooltip(data.item.id)"
            :id="`task-progress-${data.item.id}`"
            :animated="ComputeTaskUtils.progressActive(data.item.status)"
          >
            <!-- keep the progress bar value 100, otherwise the progress and text remains invisible for low progress tasks -->
            <b-progress-bar
              value="100"
              :variant="ComputeTaskUtils.progressVariant(data.item.status)"
            >
              <span>
                {{ ComputeTaskUtils.progressText(data.item) }}
              </span>
            </b-progress-bar>
          </b-progress>
          <BTooltip
            class="large-tooltip"
            placement="left"
            v-if="
              ComputeTaskUtils.progressTooltipHasContent(data.item) &&
              tooltipVisibility[data.item.id]
            "
            v-model="tooltipVisibility[data.item.id]"
            :target="`task-progress-${data.item.id}`"
            :delay="{ show: 0, hide: 30000 }"
          >
            <template
              v-if="
                data.item.status === COMPUTE_TASK_STATUS.FAILED &&
                ComputeTaskUtils.errorMessage(data.item)
              "
            >
              {{ ComputeTaskUtils.errorMessage(data.item) }}
            </template>
            <!-- BaseSpace Transfer Progress Tooltip Content -->
            <template
              v-else-if="
                data.item.taskType === COMPUTE_TASK_TYPES.BASESPACE_TRANSFER
              "
            >
              <b-table
                :items="
                  ComputeTaskUtils.fileProgressDetails_BasespaceTransfer(
                    data.item
                  )
                "
                v-if="
                  ComputeTaskUtils.fileProgressDetails_BasespaceTransfer(
                    data.item
                  ).length > 0
                "
                :fields="tooltipFieldsBasespaceTransfer"
                striped
                small
                responsive
                variant="dark"
                class="browser-table m-1"
              >
                <template #cell(fileName)="file">
                  <small>{{ file.value }}</small>
                </template>
                <template #cell(size)="file">
                  <small>{{ formatFileSize(file.value) }}</small>
                </template>
                <template #cell(progress)="file">
                  <small>{{ Math.round(file.value) }}%</small>
                </template>
              </b-table>
            </template>
            <!-- FTP Transfer Progress Tooltip Content -->
            <template
              v-else-if="data.item.taskType === COMPUTE_TASK_TYPES.FTP_TRANSFER"
            >
              <b-table
                :items="
                  ComputeTaskUtils.fileProgressDetails_FtpTranfer(data.item)
                "
                :fields="tooltipFieldsFtpTransfer"
                v-if="
                  ComputeTaskUtils.fileProgressDetails_FtpTranfer(data.item)
                    .length > 0
                "
                striped
                small
                responsive
                variant="dark"
                class="browser-table m-1"
              >
                <template #cell(filename)="file">
                  <small>{{ file.value }}</small>
                </template>
                <template #cell(progress)="file">
                  <small>{{ Math.round(file.value) }}% </small></template
                >
              </b-table>
            </template>
            <template
              v-else-if="
                data.item.status === COMPUTE_TASK_STATUS.RUNNING &&
                data.item.results.progressDescription
              "
            >
              {{ data.item.results.progressDescription }}
            </template>
          </BTooltip>
        </template>
        <!-- Data, Project, Debug, Abort -->
        <template #cell(actions)="data">
          <b-button-group>
            <!-- Data Link -->
            <b-button
              v-if="props.showBucketButton && !props.bucketId"
              size="sm"
              variant="outline-secondary"
              :id="`link-bucket-${data.item.id}`"
              :style="
                !data.item.bucket || !data.item.bucket.id ? 'opacity: 0.5' : ''
              "
              @click="bucketRequested(data.item.bucket, data.item.id, $event)"
              @click.stop
            >
              <font-awesome-icon icon="database" />
            </b-button>
            <!-- Project Link -->
            <b-button
              v-if="!props.projectId && !props.bucketId"
              size="sm"
              variant="outline-secondary"
              :id="`link-project-${data.item.id}`"
              :style="
                !data.item.project || !data.item.project.id
                  ? 'opacity: 0.5'
                  : ''
              "
              @click="projectRequested(data.item.project, data.item.id, $event)"
              @click.stop
            >
              <font-awesome-icon icon="diagram-project" />
            </b-button>
            <!-- Exec Configuration -->
            <template v-if="props.showExecDetails">
              <BButton
                :id="`task-exec-${data.item.id}`"
                variant="outline-secondary"
                size="sm"
                @click="showExecDetails(data.item)"
              >
                <font-awesome-icon :icon="['fas', 'sliders']" />
              </BButton>
            </template>
            <!-- Debug -->
            <template v-if="IS_DEBUG_MODE || userStore.developer">
              <BButton
                :id="`task-debug-${data.item.id}`"
                variant="outline-secondary"
                size="sm"
                @click="debugTaskId = data.item.id || null"
              >
                <font-awesome-icon :icon="['fas', 'code']" />
              </BButton>
            </template>
            <!-- Abort Transfer -->
            <b-button
              v-if="props.showAbortButton"
              size="sm"
              variant="outline-danger"
              :id="`link-abort-${data.item.id}`"
              :disabled="!ComputeTaskUtils.canBeAborted(data.item.status)"
              @click="taskAbortRequested(data.item)"
            >
              <font-awesome-icon icon="times" />
            </b-button>
          </b-button-group>

          <!-- Action Button Tooltips -->
          <!-- Data Link Tooltiip -->
          <BTooltip
            v-if="props.showBucketButton && !props.bucketId"
            :target="`link-bucket-${data.item.id}`"
          >
            <span v-if="data.item.bucket.deletedAt">
              <font-awesome-icon icon="exclamation-triangle" />
              Data have been deleted
            </span>
            <span v-else-if="data.item.bucket">
              <font-awesome-icon icon="database" class="me-2" />
              <strong>{{ data.item.bucket.friendlyName }}</strong>
            </span>
            <span v-else>
              <font-awesome-icon icon="exclamation-triangle" />
              No Data
            </span>
          </BTooltip>
          <!-- Project Link Tooltip -->
          <BTooltip
            v-if="!props.projectId && !props.bucketId"
            :target="`link-project-${data.item.id}`"
          >
            <span v-if="data.item.project">
              Project: <strong>{{ data.item.project.name }}</strong>
            </span>
            <span v-else>No Project</span>
          </BTooltip>
          <!-- Exec Configuration Tooltip -->
          <BTooltip
            v-if="props.showExecDetails"
            :target="`task-exec-${data.item.id}`"
            class="large-tooltip"
            placement="left"
          >
            Show exec details
          </BTooltip>
          <!-- Debug Tooltip -->
          <BTooltip
            v-if="IS_DEBUG_MODE || userStore.developer"
            :target="`task-debug-${data.item.id}`"
            class="large-tooltip"
            placement="left"
          >
            Show debug details
          </BTooltip>
          <!-- Abort Tooltip -->
          <BTooltip
            v-if="props.showAbortButton"
            :target="`link-abort-${data.item.id}`"
          >
            <span>Abort</span>
          </BTooltip>
        </template>
      </b-table>
    </BCard>

    <!-- Paging (below) -->
    <BPagination
      v-if="
        !props.pagingHideControls &&
        props.pagingItemsPerPage > 0 &&
        pagingTotalRows > props.pagingItemsPerPage
      "
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
      class="mt-2 mb-0"
    />

    <ComputeTaskDebugDetails
      v-if="debugTaskId"
      :key="debugTaskId"
      :task-id="debugTaskId"
      :project-id="props.projectId"
      @close="debugTaskId = null"
    />

    <!-- Exec Details Modal -->
    <BModal
      v-model="showDetailsModal"
      :class="[detailsContent ? 'modal-xl' : 'modal-sm']"
      @close="
        showDetailsModal = false;
        detailsContent = null;
      "
    >
      <template #header>
        <h2>
          <font-awesome-icon icon="sliders" class="me-2" />
          {{ detailsModalTitle }}
        </h2>
      </template>
      <div>
        <pre
          v-if="detailsContent"
          class="code-highlight"
          v-html="highlightJson(detailsContent)"
        ></pre>
        <p v-else>
          <font-awesome-icon icon="exclamation-triangle" />
          <strong>No Details</strong>
        </p>
      </div>
      <!-- Modal footer with close button only -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="
            showDetailsModal = false;
            detailsContent = null;
          "
        >
          Close
        </b-button>
      </template>
    </BModal>

    <!-- MODAL: Abort Compute Task -->
    <ComputeTaskAbort
      v-if="showConfirmAbortModal && selectedTask"
      :show="showConfirmAbortModal"
      :task="selectedTask"
      @close="closeTaskAbortModal"
      @close-and-refresh="closeTaskAbortModal"
    />
  </div>
</template>
